import styles from './EmailView.module.scss';

import { LinearProgress } from '@mui/material';
import Divider from '@mui/material/Divider';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import FroalaEditor from 'react-froala-wysiwyg';

import { Attachments } from '@work4all/components/lib/components/attachments';
import { AttachmentsDropZone } from '@work4all/components/lib/components/attachments/AttachmentsDropZone';
import { Collapse } from '@work4all/components/lib/components/collapse';
import { FROALA_TEXTMARKS_STYLES_URL } from '@work4all/components/lib/components/entity-preview/utils';
import { TextEditor } from '@work4all/components/lib/input/format-text/TextEditor';

import { useUser } from '@work4all/data';
import { TempFileManagerContext } from '@work4all/data/lib/hooks/data-provider/useTempFileManager';
import { useTenant } from '@work4all/data/lib/hooks/routing/TenantProvider';

import { EMail } from '@work4all/models/lib/Classes/EMail.entity';
import { ConvertTempfileTarget } from '@work4all/models/lib/Enums/ConvertTempfileTarget.enum';
import { CreateTempFileOriginType } from '@work4all/models/lib/Enums/CreateTempFileOriginType.enum';
import { EMailKind } from '@work4all/models/lib/Enums/EMailKind.enum';

import { ControllerPlus } from '../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../form-plus/use-form-context-plus';
import { useMaskContext } from '../../hooks/mask-context';
import { useEntityRightsContext } from '../../hooks/use-entity-rights';

import { Details } from './components/details/Details';
import { Message } from './components/message/Message';
import { useEmailTemplaterContext } from './email-templater/email-templater-context';
import { EmailMaskFormValue } from './types';

const generateFileUploadUrl = (baseUrl: string, authToken: string) => {
  return `${baseUrl}/api/file/inlinemailimage?auth=${authToken}`;
};

export const EmailView: React.FC = () => {
  const mask = useMaskContext<EMail>();
  const rights = useEntityRightsContext();

  const { data, isCreateMode } = mask;

  const { control, watch } = useFormContextPlus<EmailMaskFormValue>();
  const { activeTenant } = useTenant();

  const { fileList: attachmentList, uploadFiles } = useContext(
    TempFileManagerContext
  );

  const { processing } = useEmailTemplaterContext();

  const [editor, setEditor] = useState<FroalaEditor>();
  const draftMode: boolean = useMemo(() => {
    return (
      !data.id ||
      data.kind === EMailKind.ENTWURF ||
      data.kind === EMailKind.ENTWURF_HTML
    );
  }, [data]);

  useEffect(() => {
    if (editor) {
      const enabledEditing =
        (isCreateMode ? rights.create : rights.update) && draftMode;

      //ToDo: refactor this to be in TextEditor Component eg via "readOnly"-Attribute
      if (enabledEditing) {
        editor?.getEditor()?.edit?.on();
        editor?.getEditor()?.toolbar?.show();
        editor?.getEditor()?.iframe_document?.body.removeAttribute('style');
      } else {
        editor?.getEditor()?.edit?.off();
        editor?.getEditor()?.toolbar?.hide();
        editor
          ?.getEditor()
          ?.iframe_document?.body.setAttribute('style', 'user-select:text');
      }
      //resync in next renderframe after editor has been rendered initially elswise the iframe may be not as heigh as needed
      setTimeout(editor?.getEditor()?.size?.syncIframe, 0);
    }
  }, [
    data.kind,
    draftMode,
    editor,
    isCreateMode,
    rights.create,
    rights.update,
  ]);

  const user = useUser();
  const editorRef = useRef(null);

  return (
    <div className={styles.root}>
      <AttachmentsDropZone fullHeight={false}>
        <div>
          <div className={styles.mainDiv}>
            <div className={styles.message}>
              <Message />
            </div>

            <Divider className={styles.divider} orientation="vertical" />

            <div className={styles.details}>
              <Details />
            </div>
          </div>
        </div>
      </AttachmentsDropZone>
      <div className={styles.editor} ref={editorRef}>
        {attachmentList.length > 0 ? (
          <div className={styles.sectionPadding}>
            <Attachments
              disableEditAction
              disableAddAction={!draftMode}
              disableRemoveAction={!draftMode}
              layout="compact"
              fullHeight={false}
              convertFileAdditionalInfo={{
                projectId: watch('projectId'),
                businessPartnerId: watch('businessPartnerId'),
                businessPartnerType: watch('businessPartnerType'),
                contactId: watch('contactId'),
              }}
              convertTempfileTarget={ConvertTempfileTarget.DOKUMENT}
              createTempFileOriginType={
                CreateTempFileOriginType.E_MAIL_ATTACHEMENT
              }
            />
          </div>
        ) : null}
        <div>
          {draftMode !== undefined && (
            <Collapse defaultOpen={true}>
              <div style={{ position: 'relative' }}>
                <LinearProgress
                  style={{
                    display: processing ? 'unset' : 'none',
                    position: 'absolute',
                    right: '0',
                    left: '0',
                    zIndex: '999',
                    top: '-3px',
                  }}
                />
              </div>
              <ControllerPlus
                control={control}
                name="bodyHtml"
                render={({ field }) => (
                  <TextEditor
                    value={field.value}
                    onChange={field.onChange}
                    onInitialized={setEditor}
                    enableTextBuildingBlocks={true}
                    config={{
                      heightMin: 200,
                      requestHeaders: {
                        'x-work4all-mandant': activeTenant,
                      },
                      imageUpload: true,
                      imageUploadURL: generateFileUploadUrl(
                        user.baseUrl,
                        user.token
                      ),
                      iframeStyleFiles: [FROALA_TEXTMARKS_STYLES_URL],
                      events: {
                        drop: function (dropEvent) {
                          dropEvent.preventDefault();
                          uploadFiles([
                            ...dropEvent.originalEvent.dataTransfer.files,
                          ]);
                        },
                      },
                    }}
                  />
                )}
              />
              <Divider orientation="horizontal" />
            </Collapse>
          )}
        </div>
      </div>
    </div>
  );
};
